// Dashboard component - Analytics and reports section

import { useState, useEffect } from "react";
import {
  calculateKeyMetrics,
  calculatePerformanceMetrics,
  calculateWeeklyStats,
  groupByDate,
  groupByRoute,
  calculateAVR,
  calculateSeatUtilization,
} from "../../utils/calculations";
import {
  getStatisticsAdvanced,
  getDailyAnalysis,
  getBusLineAnalysis,
  getSeatUtilizationChart,
  getWeekdayStats,
  getFuelDailyChart,
  getFuelRecordsByGasStation,
  getFuelRecordsByVehicle,
} from "../../services/api";
import {
  formatDate,
  formatNumber,
  formatDecimal,
  formatPercentage,
  formatCurrency,
  getSeatUtilizationColor,
  getWeekdayName,
} from "../../utils/formatters";
import CombinedPassengersTimelineChart from "./CombinedPassengersTimelineChart";
import CombinedRevenueTimelineChart from "./CombinedRevenueTimelineChart";
import WeeklyPassengersChart from "./WeeklyPassengersChart";
import FuelCharts from "../FuelManagement/FuelCharts";
import GasStationCards from "../FuelManagement/GasStationCards";
import VehicleFuelChart from "../FuelManagement/VehicleFuelChart";
import SearchableSelect from "../common/SearchableSelect";

const Dashboard = ({ data }) => {
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [dailyAnalysis, setDailyAnalysis] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [busLineAnalysis, setBusLineAnalysis] = useState([]);
  const [loadingBusLine, setLoadingBusLine] = useState(false);
  const [seatUtilization, setSeatUtilization] = useState(null);
  const [loadingSeatUtil, setLoadingSeatUtil] = useState(false);
  const [weekdayStats, setWeekdayStats] = useState(null);
  const [loadingWeekday, setLoadingWeekday] = useState(false);
  const [dailyChartData, setDailyChartData] = useState([]);
  const [gasStationData, setGasStationData] = useState([]);
  const [vehicleFuelData, setVehicleFuelData] = useState([]);
  const [loadingFuel, setLoadingFuel] = useState(false);

  // Active Tab state
  const [activeTab, setActiveTab] = useState("bus"); // 'bus' or 'fuel'

  // Dropdown state for license plate
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Bus data
  const busData = [
    {
      id: 18,
      license_plate: "10-1208",
      device_id: "00710375E5",
      model: 2,
      miles: "59401",
    },
    {
      id: 6,
      license_plate: "10-1149",
      device_id: "008800AD2F",
      model: 1,
      miles: "470551",
    },
    {
      id: 16,
      license_plate: "10-1205",
      device_id: "007101FAEA",
      model: 2,
      miles: "145135",
    },
    {
      id: 28,
      license_plate: "10-1264",
      device_id: "009900B327",
      model: 2,
      miles: "4755",
    },
    {
      id: 20,
      license_plate: "10-1210",
      device_id: "00710375E4",
      model: 2,
      miles: "136299",
    },
    { id: 11, license_plate: "10-1154", device_id: "0", model: 1, miles: "1" },
    {
      id: 9,
      license_plate: "10-1152",
      device_id: "0088007439",
      model: 1,
      miles: "419661",
    },
    {
      id: 12,
      license_plate: "10-1155",
      device_id: "008800AB63",
      model: 1,
      miles: "2",
    },
    {
      id: 10,
      license_plate: "10-1153",
      device_id: "009900B316",
      model: 1,
      miles: "434421",
    },
    {
      id: 23,
      license_plate: "10-1219",
      device_id: "007103AF42",
      model: 2,
      miles: "761010",
    },
    {
      id: 29,
      license_plate: "10-1265",
      device_id: "009900B31B",
      model: 2,
      miles: "8048",
    },
    {
      id: 22,
      license_plate: "10-1218",
      device_id: "007103AF1E",
      model: 2,
      miles: "53315",
    },
    {
      id: 27,
      license_plate: "10-1223",
      device_id: "007103AF3C",
      model: 2,
      miles: "54259",
    },
    {
      id: 17,
      license_plate: "10-1207",
      device_id: "007101FAD2",
      model: 2,
      miles: "106263",
    },
    {
      id: 26,
      license_plate: "10-1222",
      device_id: "007103AF23",
      model: 2,
      miles: "60645",
    },
    {
      id: 15,
      license_plate: "10-1204",
      device_id: "007101FAF8",
      model: 2,
      miles: "183570",
    },
    {
      id: 5,
      license_plate: "10-1148",
      device_id: "008800B133",
      model: 1,
      miles: "397258",
    },
    {
      id: 21,
      license_plate: "10-1212",
      device_id: "00710375E9",
      model: 2,
      miles: "136204",
    },
    {
      id: 8,
      license_plate: "10-1151",
      device_id: "008800AD15",
      model: 1,
      miles: "418431",
    },
    {
      id: 25,
      license_plate: "10-1221",
      device_id: "007103AF28",
      model: 2,
      miles: "65296",
    },
    {
      id: 4,
      license_plate: "10-1147",
      device_id: "008800B1A2",
      model: 1,
      miles: "446697",
    },
    {
      id: 7,
      license_plate: "10-1150",
      device_id: "0099018E5E",
      model: 1,
      miles: "446697",
    },
    {
      id: 19,
      license_plate: "10-1209",
      device_id: "00710375E0",
      model: 2,
      miles: "74427",
    },
    {
      id: 13,
      license_plate: "10-1159",
      device_id: "008800AD4A",
      model: 1,
      miles: "357651",
    },
    {
      id: 14,
      license_plate: "10-1196",
      device_id: "0099018DF8",
      model: 2,
      miles: "174586",
    },
    {
      id: 24,
      license_plate: "10-1220",
      device_id: "007103AF46",
      model: 2,
      miles: "62261",
    },
  ];

  // Filter bus data based on search term
  const filteredBusData = busData.filter((bus) =>
    bus.license_plate.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Filters state
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    licensePlate: "",
    busLine: "",
    selectedYears: [2026, 2025, 2024],
  });

  // Fetch advanced statistics from API
  useEffect(() => {
    const fetchAdvancedStats = async () => {
      setLoadingStats(true);
      try {
        const result = await getStatisticsAdvanced(
          filters.startDate,
          filters.endDate,
          filters.busLine,
          filters.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );
        if (result.success && result.data) {
          setAdvancedStats(result.data);
        }
      } catch (error) {
        console.error("Error fetching advanced statistics:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    if (data && data.length > 0) {
      fetchAdvancedStats();
    }
  }, [data, filters]);

  // Fetch daily analysis from API
  useEffect(() => {
    const fetchDailyAnalysis = async () => {
      setLoadingDaily(true);
      try {
        const result = await getDailyAnalysis(
          filters.startDate,
          filters.endDate,
          filters.busLine,
          filters.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );
        if (result.success && result.data) {
          setDailyAnalysis(result.data);
        }
      } catch (error) {
        console.error("Error fetching daily analysis:", error);
      } finally {
        setLoadingDaily(false);
      }
    };

    if (data && data.length > 0) {
      fetchDailyAnalysis();
    }
  }, [data, filters]);

  // Fetch bus line analysis from API
  useEffect(() => {
    const fetchBusLineAnalysis = async () => {
      setLoadingBusLine(true);
      try {
        const result = await getBusLineAnalysis(
          filters.startDate,
          filters.endDate,
          filters.busLine,
          filters.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );
        if (result.success && result.data) {
          setBusLineAnalysis(result.data);
        }
      } catch (error) {
        console.error("Error fetching bus line analysis:", error);
      } finally {
        setLoadingBusLine(false);
      }
    };

    if (data && data.length > 0) {
      fetchBusLineAnalysis();
    }
  }, [data, filters]);

  // Fetch seat utilization from API
  useEffect(() => {
    const fetchSeatUtilization = async () => {
      setLoadingSeatUtil(true);
      try {
        const result = await getSeatUtilizationChart(
          filters.startDate,
          filters.endDate,
          filters.busLine,
          filters.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );
        if (result.success && result.data) {
          const data = result.data;

          // Handle new API structure with summary array
          if (data.summary && Array.isArray(data.summary)) {
            const highData =
              data.summary.find((item) => item.level === "high") || {};
            const mediumData =
              data.summary.find((item) => item.level === "medium") || {};
            const lowData =
              data.summary.find((item) => item.level === "low") || {};

            const mapped = {
              high_utilization_count: highData.count || 0,
              medium_utilization_count: mediumData.count || 0,
              low_utilization_count: lowData.count || 0,
            };
            setSeatUtilization(mapped);
          } else {
            // Fallback to old structure
            const mapped = {
              high_utilization_count: data.high?.count || 0,
              medium_utilization_count: data.medium?.count || 0,
              low_utilization_count: data.low?.count || 0,
            };
            setSeatUtilization(mapped);
          }
        }
      } catch (error) {
        console.error("Error fetching seat utilization:", error);
      } finally {
        setLoadingSeatUtil(false);
      }
    };

    if (data && data.length > 0) {
      fetchSeatUtilization();
    }
  }, [data, filters]);

  // Fetch weekday stats from API
  useEffect(() => {
    const fetchWeekdayStats = async () => {
      setLoadingWeekday(true);
      try {
        const result = await getWeekdayStats(
          filters.startDate,
          filters.endDate,
          filters.busLine,
          filters.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );
        if (result.success && result.data) {
          setWeekdayStats(result.data);
        }
      } catch (error) {
        console.error("Error fetching weekday stats:", error);
      } finally {
        setLoadingWeekday(false);
      }
    };

    if (data && data.length > 0) {
      fetchWeekdayStats();
    }
  }, [data, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".license-plate-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Fetch fuel daily chart data
  useEffect(() => {
    const fetchFuelData = async () => {
      setLoadingFuel(true);
      try {
        const [dailyResult, stationResult, vehicleResult] = await Promise.all([
          getFuelDailyChart(
            filters.startDate,
            filters.endDate,
            filters.licensePlate,
          ),
          getFuelRecordsByGasStation(
            filters.startDate,
            filters.endDate,
            filters.licensePlate,
          ),
          getFuelRecordsByVehicle(
            filters.startDate,
            filters.endDate,
            filters.licensePlate,
          ),
        ]);

        if (dailyResult.success && dailyResult.data) {
          setDailyChartData(dailyResult.data);
        }

        if (stationResult.success && stationResult.data) {
          setGasStationData(stationResult.data);
        }

        if (vehicleResult.success && vehicleResult.data) {
          setVehicleFuelData(vehicleResult.data);
        }
      } catch (error) {
        console.error("Error fetching fuel data:", error);
      } finally {
        setLoadingFuel(false);
      }
    };

    fetchFuelData();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // Set all loading states to true when filter changes
    setLoadingStats(true);
    setLoadingDaily(true);
    setLoadingBusLine(true);
    setLoadingSeatUtil(true);
    setLoadingWeekday(true);
    if (activeTab === "fuel") {
      setLoadingFuel(true);
    }

    // Allow both date range and year selection at the same time
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      licensePlate: "",
      busLine: "",
      selectedYears: [],
    });
    setSearchTerm("");
  };

  const handleSelectBus = (licensePlate) => {
    // Set loading states when selecting a bus
    setLoadingStats(true);
    setLoadingDaily(true);
    setLoadingBusLine(true);
    setLoadingSeatUtil(true);
    setLoadingWeekday(true);
    setLoadingFuel(true);
    setFilters({
      ...filters,
      licensePlate: licensePlate,
    });
    setSearchTerm(licensePlate);
    setIsDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <svg
          className="w-16 h-16 mx-auto mb-4 text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
        </svg>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          ไม่มีข้อมูลสำหรับแสดงผล
        </h3>
        <p className="text-gray-500">
          กรุณาเพิ่มข้อมูลในแท็บ &quot;จัดการข้อมูลรถ&quot; ก่อน
        </p>
      </div>
    );
  }

  // Use API data if available, fallback to local calculations
  const metrics = advancedStats || calculateKeyMetrics(data);
  const performance = seatUtilization || calculatePerformanceMetrics(data);
  const weeklyStatsLocal = calculateWeeklyStats(data);
  const dailyData = groupByDate(data);
  const routeData = groupByRoute(data);

  // Fixed bus lines - only show specific routes
  const busLines = ["สนามบิน - ราไวย์", "บขส.1 - ป่าตอง", "รอบเมืองเก่า"];

  // Sort data for tables
  const sortedDates = Object.keys(dailyData).sort(
    (a, b) => new Date(b) - new Date(a),
  );
  const sortedRoutes = Object.keys(routeData).sort(
    (a, b) => routeData[b].totalPassengers - routeData[a].totalPassengers,
  );

  // Check if any data is loading
  const isAnyLoading =
    activeTab === "bus"
      ? loadingStats ||
        loadingDaily ||
        loadingBusLine ||
        loadingSeatUtil ||
        loadingWeekday
      : loadingFuel;

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {isAnyLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform animate-pulse">
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-20 h-20 border-8 border-indigo-200 rounded-full"></div>
                <div className="w-20 h-20 border-8 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
              <h3 className="mt-6 text-xl font-bold text-gray-800">
                กำลังโหลดข้อมูล...
              </h3>
              <p className="mt-2 text-gray-500 text-center">
                กรุณารอสักครู่ ระบบกำลังประมวลผลข้อมูลของคุณ
              </p>
              <div className="mt-4 flex gap-2">
                <div
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-bold mb-3 flex items-center">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-3 mr-4 shadow-lg">
              <svg
                className="w-10 h-10"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-100">
              Dashboard วิเคราะห์ข้อมูล
            </span>
          </h2>
          <p className="text-purple-50 text-lg ml-20 font-light">
            วิเคราะห์ประสิทธิภาพการให้บริการรถประจำทาง
            และการจัดการน้ำมันอย่างครบวงจร
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border-2 border-indigo-100 hover:border-indigo-200 transition-all duration-300">
        <div className="flex items-center mb-6">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg
              className="w-6 h-6 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-xl font-bold text-gray-800">ตัวกรองข้อมูล</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              เลือกช่วงเวลาและรถที่ต้องการวิเคราะห์
            </p>
          </div>
        </div>

        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${activeTab === "bus" ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}
        >
          {/* Start Date */}
          <div className="group">
            <label className="text-sm font-bold text-gray-700 mb-2.5 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mr-2 shadow-md group-hover:scale-110 transition-transform">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              วันที่เริ่มต้น
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all duration-200 hover:border-purple-300 bg-gray-50 focus:bg-white"
            />
          </div>

          {/* End Date */}
          <div className="group">
            <label className="text-sm font-bold text-gray-700 mb-2.5 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mr-2 shadow-md group-hover:scale-110 transition-transform">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              วันที่สิ้นสุด
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all duration-200 hover:border-purple-300 bg-gray-50 focus:bg-white"
            />
          </div>

          {/* License Plate Dropdown - Show only in fuel tab */}
          {activeTab === "fuel" && (
            <div className="group relative license-plate-dropdown">
              <label className="text-sm font-bold text-gray-700 mb-2.5 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-lg flex items-center justify-center mr-2 shadow-md group-hover:scale-110 transition-transform">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                  </svg>
                </div>
                ทะเบียนรถ
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder="ค้นหาทะเบียนรถ..."
                  className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all duration-200 hover:border-purple-300 bg-gray-50 focus:bg-white placeholder-gray-400"
                />
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
                {isDropdownOpen && filteredBusData.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white border-2 border-indigo-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    <div className="p-2">
                      {filteredBusData.map((bus) => (
                        <button
                          key={bus.id}
                          onClick={() => handleSelectBus(bus.license_plate)}
                          className="w-full text-left px-4 py-3 hover:bg-indigo-50 rounded-lg transition-colors duration-150 flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg flex items-center justify-center group-hover:from-indigo-200 group-hover:to-indigo-300 transition-all">
                              <svg
                                className="w-5 h-5 text-indigo-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-bold text-gray-800 group-hover:text-indigo-600 transition-colors">
                                {bus.license_plate}
                              </p>
                              <p className="text-xs text-gray-500">
                                Model {bus.model} •{" "}
                                {parseInt(bus.miles).toLocaleString()} km
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {filters.licensePlate && (
                <button
                  onClick={() => {
                    setFilters({ ...filters, licensePlate: "" });
                    setSearchTerm("");
                  }}
                  className="absolute right-12 top-[52px] text-gray-400 hover:text-red-500 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Bus Line Filter - Show only in bus tab */}
          {activeTab === "bus" && (
            <SearchableSelect
              name="busLine"
              value={filters.busLine}
              onChange={handleFilterChange}
              options={busLines.map((line) => ({
                value: line,
                label: line,
              }))}
              placeholder="ทุกสาย"
              label="สายรถ"
              icon={
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                    clipRule="evenodd"
                  />
                </svg>
              }
            />
          )}

          {/* Year Filter - Multi-select - Show only in bus tab */}
          {activeTab === "bus" && (
            <div className="group col-span-full">
              <label className="text-sm font-bold text-gray-700 mb-2.5 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center mr-2 shadow-md group-hover:scale-110 transition-transform">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                เลือกปีเพื่อเปรียบเทียบ (สามารถเลือกได้หลายปี)
              </label>
              <div className="flex flex-wrap gap-2">
                {[2026, 2025, 2024].map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => {
                      // Set loading states when selecting year
                      setLoadingStats(true);
                      setLoadingDaily(true);
                      setLoadingBusLine(true);
                      setLoadingSeatUtil(true);
                      setLoadingWeekday(true);

                      setFilters((prev) => {
                        const selected = prev.selectedYears || [];
                        const isSelected = selected.includes(year);
                        return {
                          ...prev,
                          selectedYears: isSelected
                            ? selected.filter((y) => y !== year)
                            : [...selected, year],
                        };
                      });
                    }}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                      (filters.selectedYears || []).includes(year)
                        ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg transform scale-105"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {year} {(filters.selectedYears || []).includes(year) && "✓"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reset Button */}
          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-bold px-4 py-3 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clipRule="evenodd"
                />
              </svg>
              รีเซ็ต
            </button>
          </div>
        </div>
      </div>

      {/* ========== Tab Navigation ========== */}
      <div className="bg-white rounded-2xl shadow-xl p-2 mb-8 border-2 border-gray-100">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("bus")}
            className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
              activeTab === "bus"
                ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            }`}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
            </svg>
            วิเคราะห์ผู้โดยสาร
          </button>
          <button
            onClick={() => setActiveTab("fuel")}
            className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
              activeTab === "fuel"
                ? "bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg transform scale-105"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            }`}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
              />
            </svg>
            วิเคราะห์น้ำมัน
          </button>
        </div>
      </div>

      {/* ========== ส่วนที่ 1: วิเคราะห์ผู้โดยสาร ========== */}
      {activeTab === "bus" && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 mb-8 border-2 border-blue-200">
          {/* Section Header */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-blue-800 mb-2 flex items-center">
              <svg
                className="w-7 h-7 mr-3 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
              </svg>
              วิเคราะห์ผู้โดยสาร
            </h3>
            <p className="text-blue-700 ml-10">
              ข้อมูลผู้โดยสาร เส้นทาง และประสิทธิภาพการให้บริการ
            </p>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Card 1: AVR/Round */}
            <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-cyan-100 hover:border-cyan-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-7 h-7 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-2">
                  AVR/Round เฉลี่ย
                </p>
                <p className="text-4xl font-bold text-gray-800 mb-1">
                  {formatDecimal(
                    advancedStats?.avg_round ||
                      metrics.avgPassengersPerTrip ||
                      0,
                  )}
                </p>
                <p className="text-cyan-600 text-sm font-medium">คน/เที่ยว</p>
              </div>
            </div>

            {/* Card 2: AVR%/Seats */}
            <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-emerald-100 hover:border-emerald-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-7 h-7 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-2">
                  AVR%/Seats เฉลี่ย
                </p>
                <p className="text-4xl font-bold text-gray-800 mb-1">
                  {formatPercentage(
                    advancedStats?.avg_seat !== undefined
                      ? advancedStats.avg_seat
                      : (metrics.avgSeatUtilization || 0) * 100,
                  )}
                </p>
                <p className="text-emerald-600 text-sm font-medium">
                  การใช้ที่นั่ง
                </p>
              </div>
            </div>

            {/* Card 3: Top Date */}
            <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-rose-100 hover:border-rose-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-400 to-rose-600 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-7 h-7 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-2">
                  วันที่มีผู้โดยสารมากที่สุด
                </p>
                <p className="text-2xl font-bold text-gray-800 mb-1">
                  {advancedStats?.top_date?.date
                    ? formatDate(advancedStats.top_date.date)
                    : metrics.peakDay
                      ? formatDate(metrics.peakDay)
                      : "-"}
                </p>
                <p className="text-rose-600 text-sm font-medium">
                  {formatNumber(
                    advancedStats?.top_date?.passengers ||
                      metrics.peakDayPassengers ||
                      0,
                  )}{" "}
                  คน
                </p>
              </div>
            </div>

            {/* Card 4: Top Route */}
            <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-amber-100 hover:border-amber-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400 to-amber-600 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-7 h-7 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-2">
                  สายที่มีผู้โดยสารมากที่สุด
                </p>
                <p className="text-2xl font-bold text-gray-800 mb-1">
                  {advancedStats?.top_bus_line?.bus_line ||
                    metrics.topRoute ||
                    "-"}
                </p>
                <p className="text-amber-600 text-sm font-medium">
                  {formatNumber(
                    advancedStats?.top_bus_line?.passengers ||
                      metrics.topRoutePassengers ||
                      0,
                  )}{" "}
                  คน
                </p>
              </div>
            </div>
          </div>

          {/* Analysis Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Analysis */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-blue-100 hover:border-blue-200 transition-all duration-300">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                <h3 className="text-xl font-bold text-blue-800 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  วิเคราะห์รายวัน
                </h3>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-100 to-indigo-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-blue-900 uppercase tracking-wider">
                        วันที่
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">
                        ผู้โดยสาร
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">
                        เที่ยว
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">
                        AVR/Round
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">
                        AVR%/Seats
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(dailyAnalysis.length > 0
                      ? dailyAnalysis
                      : sortedDates.slice(0, 10)
                    ).map((item) => {
                      // Use API data if available, otherwise fallback to local data
                      const date = item.date || item;
                      const passengers =
                        item.passengers ||
                        dailyData[date]?.totalPassengers ||
                        0;
                      const trips =
                        item.trips || dailyData[date]?.totalTrips || 0;
                      const avr =
                        item.avr_round || calculateAVR(passengers, trips);
                      const seatUtilization =
                        item.avr_seat !== undefined
                          ? item.avr_seat
                          : calculateSeatUtilization(avr);

                      return (
                        <tr
                          key={date}
                          className="hover:bg-blue-50 transition-colors duration-150"
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {formatDate(date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-blue-700 text-center font-bold">
                            {formatNumber(passengers)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-center font-medium">
                            {formatNumber(trips)}
                          </td>
                          <td className="px-4 py-3 text-sm text-indigo-700 text-center font-bold">
                            {formatDecimal(avr)}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span
                              className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${getSeatUtilizationColor(seatUtilization)}`}
                            >
                              {formatPercentage(seatUtilization)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Route Analysis */}
            {/* Bus Line Analysis */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-purple-100 hover:border-purple-200 transition-all duration-300">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                <h3 className="text-xl font-bold text-purple-800 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                  </svg>
                  วิเคราะห์รายสาย
                </h3>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-purple-100 to-pink-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-purple-900 uppercase tracking-wider">
                        สายรถ
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-purple-900 uppercase tracking-wider">
                        ผู้โดยสาร
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-purple-900 uppercase tracking-wider">
                        เที่ยว
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-purple-900 uppercase tracking-wider">
                        AVR/Round
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-purple-900 uppercase tracking-wider">
                        AVR%/Seats
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(busLineAnalysis.length > 0
                      ? busLineAnalysis
                      : sortedRoutes
                    ).map((item) => {
                      // Use API data if available, otherwise fallback to local data
                      const route = item.bus_line || item;
                      const passengers =
                        item.passengers ||
                        routeData[route]?.totalPassengers ||
                        0;
                      const trips =
                        item.trips || routeData[route]?.totalTrips || 0;
                      const avr =
                        item.avr_round || calculateAVR(passengers, trips);
                      const seatUtilization =
                        item.avr_seat !== undefined
                          ? item.avr_seat
                          : calculateSeatUtilization(avr);

                      return (
                        <tr
                          key={route}
                          className="hover:bg-purple-50 transition-colors duration-150"
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 font-bold">
                            {route}
                          </td>
                          <td className="px-4 py-3 text-sm text-purple-700 text-center font-bold">
                            {formatNumber(passengers)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-center font-medium">
                            {formatNumber(trips)}
                          </td>
                          <td className="px-4 py-3 text-sm text-indigo-700 text-center font-bold">
                            {formatDecimal(avr)}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span
                              className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${getSeatUtilizationColor(seatUtilization)}`}
                            >
                              {formatPercentage(seatUtilization)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Timeline Charts - Reordered */}
          <div className="space-y-6 mb-6">
            <CombinedPassengersTimelineChart data={data} filters={filters} />
            <CombinedRevenueTimelineChart data={data} filters={filters} />
          </div>

          {/* Weekly Passengers Chart */}
          <WeeklyPassengersChart data={data} filters={filters} />

          {/* Performance Metrics */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-green-100 hover:border-green-200 transition-all duration-300 mt-4">
            <div className="flex items-center mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-2xl font-bold text-gray-800">
                  ข้อมูลการใช้ที่นั่ง
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  สรุปจำนวนวันตามช่วงประสิทธิภาพการใช้ที่นั่ง
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* High Utilization */}
              <div className="group relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 hover:border-green-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                  <svg
                    className="w-16 h-16 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="relative">
                  <div className="flex items-center mb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2 shadow-lg"></div>
                    <p className="text-sm font-bold text-green-800 uppercase tracking-wide">
                      ประสิทธิภาพสูง
                    </p>
                  </div>
                  <div className="text-sm text-green-700 font-semibold flex flex-col">
                    <span>มากกว่า 70% </span>
                    <div className="text-5xl font-extrabold text-green-600 mb-2">
                      {formatNumber(
                        seatUtilization?.high_utilization_count ||
                          performance.highUtilizationCount ||
                          0,
                      )}{" "}
                      รอบ
                    </div>
                    <span>
                      ( คิดเป็น{" "}
                      {(() => {
                        const total =
                          (seatUtilization?.high_utilization_count || 0) +
                          (seatUtilization?.medium_utilization_count || 0) +
                          (seatUtilization?.low_utilization_count || 0);
                        return total > 0
                          ? Math.round(
                              ((seatUtilization?.high_utilization_count || 0) /
                                total) *
                                100,
                            )
                          : 0;
                      })()}
                      % ของทั้งหมด )
                    </span>
                  </div>
                </div>
              </div>

              {/* Medium Utilization */}
              <div className="group relative bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6 border-2 border-yellow-200 hover:border-yellow-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                  <svg
                    className="w-16 h-16 text-yellow-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="relative">
                  <div className="flex items-center mb-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2 shadow-lg"></div>
                    <p className="text-sm font-bold text-yellow-800 uppercase tracking-wide">
                      ประสิทธิภาพปานกลาง
                    </p>
                  </div>
                  <div className="text-sm text-yellow-700 font-semibold flex flex-col">
                    <span>50% - 70% </span>
                    <div className="text-5xl font-extrabold text-yellow-600 mb-2">
                      {formatNumber(
                        seatUtilization?.medium_utilization_count ||
                          performance.mediumUtilizationCount ||
                          0,
                      )}{" "}
                      รอบ
                    </div>
                    <span>
                      ( คิดเป็น{" "}
                      {(() => {
                        const total =
                          (seatUtilization?.high_utilization_count || 0) +
                          (seatUtilization?.medium_utilization_count || 0) +
                          (seatUtilization?.low_utilization_count || 0);
                        return total > 0
                          ? Math.round(
                              ((seatUtilization?.medium_utilization_count ||
                                0) /
                                total) *
                                100,
                            )
                          : 0;
                      })()}
                      % ของทั้งหมด )
                    </span>
                  </div>
                </div>
              </div>

              {/* Low Utilization */}
              <div className="group relative bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border-2 border-red-200 hover:border-red-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                  <svg
                    className="w-16 h-16 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="relative">
                  <div className="flex items-center mb-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2 shadow-lg"></div>
                    <p className="text-sm font-bold text-red-800 uppercase tracking-wide">
                      ประสิทธิภาพต่ำ
                    </p>
                  </div>
                  <div className="text-sm text-red-700 font-semibold flex flex-col">
                    <span>น้อยกว่า 50% </span>
                    <div className="text-5xl font-extrabold text-red-600 mb-2">
                      {formatNumber(
                        seatUtilization?.low_utilization_count ||
                          performance.lowUtilizationCount ||
                          0,
                      )}{" "}
                      รอบ
                    </div>
                    <span>
                      ( คิดเป็น{" "}
                      {(() => {
                        const total =
                          (seatUtilization?.high_utilization_count || 0) +
                          (seatUtilization?.medium_utilization_count || 0) +
                          (seatUtilization?.low_utilization_count || 0);
                        return total > 0
                          ? Math.round(
                              ((seatUtilization?.low_utilization_count || 0) /
                                total) *
                                100,
                            )
                          : 0;
                      })()}
                      % ของทั้งหมด )
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekday Statistics */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-indigo-100 hover:border-indigo-200 transition-all duration-300">
            <div className="flex items-center mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-2xl font-bold text-gray-800">
                  สถิติตามวันในสัปดาห์
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  เปรียบเทียบข้อมูลผู้โดยสารในแต่ละวัน
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Weekday Average */}
              <div className="group relative bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-3 right-3">
                  <svg
                    className="w-10 h-10 text-blue-300 opacity-30 group-hover:opacity-50 transition-opacity"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" />
                  </svg>
                </div>
                <div className="relative">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">
                    เฉลี่ยวันธรรมดา (จันทร์ - ศุกร์)
                  </p>
                  <div className="text-3xl font-extrabold text-blue-600 mb-1">
                    {formatNumber(
                      Math.round(
                        weekdayStats?.weekday_average?.avg_passengers ||
                          weeklyStatsLocal.weekdayAvg ||
                          0,
                      ),
                    )}
                  </div>
                  <p className="text-sm text-blue-600 font-semibold">คน/วัน</p>
                </div>
              </div>

              {/* Weekend Average */}
              <div className="group relative bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border-2 border-orange-200 hover:border-orange-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-3 right-3">
                  <svg
                    className="w-10 h-10 text-orange-300 opacity-30 group-hover:opacity-50 transition-opacity"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                  </svg>
                </div>
                <div className="relative">
                  <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-2">
                    เฉลี่ยวันหยุด (เสาร์ - อาทิตย์)
                  </p>
                  <div className="text-3xl font-extrabold text-orange-600 mb-1">
                    {formatNumber(
                      Math.round(
                        weekdayStats?.weekend_average?.avg_passengers ||
                          weeklyStatsLocal.weekendAvg ||
                          0,
                      ),
                    )}
                  </div>
                  <p className="text-sm text-orange-600 font-semibold">
                    คน/วัน
                  </p>
                </div>
              </div>

              {/* Peak Day */}
              <div className="group relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 hover:border-green-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-3 right-3">
                  <svg
                    className="w-10 h-10 text-green-300 opacity-30 group-hover:opacity-50 transition-opacity"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="relative">
                  <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">
                    วันที่คนมากที่สุด
                  </p>
                  <div className="text-3xl font-extrabold text-green-600 mb-1">
                    {weekdayStats?.max_day?.day_name ||
                      getWeekdayName(weeklyStatsLocal.peakDay)}
                  </div>
                  <p className="text-sm text-green-600 font-semibold">
                    {formatNumber(weekdayStats?.max_day?.passengers || 0)} คน
                  </p>
                </div>
              </div>

              {/* Low Day */}
              <div className="group relative bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-6 border-2 border-red-200 hover:border-red-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="absolute top-3 right-3">
                  <svg
                    className="w-10 h-10 text-red-300 opacity-30 group-hover:opacity-50 transition-opacity"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="relative">
                  <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">
                    วันที่คนน้อยที่สุด
                  </p>
                  <div className="text-3xl font-extrabold text-red-600 mb-1">
                    {weekdayStats?.min_day?.day_name ||
                      getWeekdayName(weeklyStatsLocal.lowDay)}
                  </div>
                  <p className="text-sm text-red-600 font-semibold">
                    {formatNumber(weekdayStats?.min_day?.passengers || 0)} คน
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ========== สิ้นสุดส่วนวิเคราะห์ข้อมูลรถ ========== */}

      {/* ========== ส่วนที่ 2: วิเคราะห์ข้อมูลน้ำมัน ========== */}
      {activeTab === "fuel" &&
        (gasStationData.length > 0 ||
          dailyChartData.length > 0 ||
          vehicleFuelData.length > 0) && (
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl shadow-2xl p-8 mb-8 border-2 border-blue-200 hover:border-blue-300 transition-all duration-300">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-72 h-72 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
              <div
                className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-400 rounded-full mix-blend-overlay filter blur-3xl animate-pulse"
                style={{ animationDelay: "1s" }}
              ></div>
            </div>

            {/* Section Header */}
            <div className="relative z-10 mb-8">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                    วิเคราะห์ข้อมูลน้ำมัน
                  </h3>
                  <p className="text-blue-700 font-medium mt-1">
                    สถิติการเติมน้ำมัน ปั๊มน้ำมัน และแนวโน้มราคา
                  </p>
                </div>
              </div>
            </div>

            {/* Gas Station Cards */}
            {gasStationData.length > 0 && (
              <div className="relative z-10 mb-8">
                <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-blue-200">
                  <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                    สรุปตามปั๊มน้ำมัน
                  </h4>
                  <GasStationCards data={gasStationData} />
                </div>
              </div>
            )}

            {/* Vehicle Fuel Summary Cards */}
            {vehicleFuelData.length > 0 && (
              <div className="relative z-10 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-orange-700 uppercase">
                        ยอดรวมเงิน
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatCurrency(
                        vehicleFuelData.reduce(
                          (sum, item) => sum + (item.total_amount || 0),
                          0,
                        ),
                      )}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-blue-700 uppercase">
                        ยอดรวมปริมาณ
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatDecimal(
                        vehicleFuelData.reduce(
                          (sum, item) => sum + (item.total_volume || 0),
                          0,
                        ),
                      )}{" "}
                      ลิตร
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                          <path
                            fillRule="evenodd"
                            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-green-700 uppercase">
                        ยอดรวมครั้ง
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatNumber(
                        vehicleFuelData.reduce(
                          (sum, item) => sum + (item.fill_count || 0),
                          0,
                        ),
                      )}{" "}
                      ครั้ง
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-purple-700 uppercase">
                        จำนวนรถ
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">
                      {
                        [
                          ...new Set(
                            vehicleFuelData.map((item) => item.license_plate),
                          ),
                        ].length
                      }{" "}
                      คัน
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Fuel Charts */}
            {dailyChartData.length > 0 && (
              <div className="relative z-10 mb-8">
                <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-blue-200">
                  <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                    กราฟแนวโน้มราคาและปริมาณน้ำมัน
                  </h4>
                  <FuelCharts dailyChartData={dailyChartData} />
                </div>
              </div>
            )}

            {/* Vehicle Fuel Chart */}
            {vehicleFuelData.length > 0 && (
              <div className="relative z-10">
                <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-blue-200">
                  <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                    </svg>
                    ค่าน้ำมันแยกตามรถ
                  </h4>
                  <VehicleFuelChart vehicleFuelData={vehicleFuelData} />
                </div>
              </div>
            )}
          </div>
        )}

      {/* ========== แสดงข้อความเมื่อไม่มีข้อมูลน้ำมัน ========== */}
      {activeTab === "fuel" &&
        gasStationData.length === 0 &&
        dailyChartData.length === 0 &&
        vehicleFuelData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-16 text-center border-2 border-gray-200">
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
                <svg
                  className="w-12 h-12 text-blue-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                ไม่มีข้อมูลน้ำมัน
              </h3>
              <p className="text-gray-500 mb-6">
                กรุณาเลือกช่วงเวลาที่มีข้อมูลการเติมน้ำมัน
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

export default Dashboard;
